import { useState } from 'react';

// Material UI
import { styled, Box, Typography } from '@mui/material';
import { Alert } from '@mui/material';
import { Card, CardHeader, CardContent } from '@mui/material';

// Components
import ProductPicker from 'components/orders/ProductPicker';
import SeatingChart from 'components/SeatingChart';
import CompFormCustomer from 'components/comp/CompFormCustomer';
import { ScrollRestoreContainer } from 'components/ui/layout';
import { SubmitButton } from 'components/ui/buttons';
import { CancelButton } from 'components/ui/buttons';

// Utility
import { validateString, validateEmail, validateSelect } from 'utility/validation';
import { getCountryProfile, isValidPhone, isValidPostalCode } from 'utility/countries';
import { useNotification } from 'context/notification';

export default function CompForm(props) {
    const {
        eventId,
        seatsEnabled,
        cart,
        customer,
        sessionExpired,
        onCartProductUpdate,
        onCartBundleUpdate,
        onCustomerUpdate,
        onCartSeatAdd,
        onCartSeatRemove,
        onSessionInitialized,
        onSessionExpired,
        onSubmit,
        onCancel,
    } = props;

    const { notify } = useNotification();

    const [emptyCart, setEmptyCart] = useState(false);

    const [validFields, setValidFields] = useState({
        firstName: true,
        lastName: true,
        email: true,
        phone: true,
        company: true,
        address: true,
        city: true,
        province: true,
        postalCode: true,
        country: true,
    });

    const handleSubmit = () => {
        const {
            firstName,
            lastName,
            email,
            phone,
            company,
            address,
            city,
            province,
            postalCode,
            country,
        } = props.customer;
        const countryProfile = getCountryProfile(country);

        const newValidFields = {
            // Mandatory Fields
            firstName: validateString(firstName),
            lastName: validateString(lastName),
            email: validateEmail(email),
            // Optional Fields
            phone: isValidPhone(phone, country) || phone.trim() === '',
            company: validateString(company) || company.trim() === '',
            address: validateString(address) || address.trim() === '',
            city: validateString(city) || city.trim() === '',
            province:
                countryProfile.region.mode === 'hidden' ||
                validateSelect(province) ||
                province.trim() === '',
            postalCode: isValidPostalCode(postalCode, country) || postalCode.trim() === '',
            country: validateSelect(country) || country.trim() === '',
        };

        const emptyCart =
            cart.products.every((c) => c.quantity === 0) &&
            cart.bundles.every((c) => c.quantity === 0);
        const customerValid = Object.keys(newValidFields).every(
            (key) => newValidFields[key] === true,
        );
        const emptySeats = seatsEnabled && cart.seats.length === 0;

        let ticketsSelected = false;

        if (seatsEnabled) {
            // Assigned Seats Enabled: Validate seating chart and ticket cart
            ticketsSelected = !emptyCart || !emptySeats;
        } else {
            // Regular Ticketed Event: Only validate ticket cart
            ticketsSelected = !emptyCart;
        }

        if (ticketsSelected === true && customerValid === true) {
            onSubmit();
        } else {
            setEmptyCart(emptyCart);
            setValidFields(newValidFields);
            if (emptyCart || emptySeats) {
                notify.warning('Please select at least one ticket');
            } else {
                notify.warning('Please make sure every field is filled in correctly');
            }
        }
    };

    const seatProductsAvailable = Boolean(cart.seatProducts.length > 0);

    return (
        <ScrollRestoreContainer>
            <FormContainer>
                <Box mb={4}>
                    <Alert severity="warning">
                        Keep in mind: Use this feature to create complimentary orders that don't
                        require payment. It allows you to bypass restrictions like sold-out, hidden,
                        or currently unavailable tickets.
                    </Alert>
                </Box>

                {seatsEnabled && (
                    <Box mb={4}>
                        <Card>
                            <CardHeader title="Assigned Seating" />
                            <CardContent>
                                {seatProductsAvailable ? (
                                    <SeatingChart
                                        eventId={eventId}
                                        modes={['createOrder']}
                                        pricing={cart.seatPricing}
                                        channelKeys={cart.channelKeys}
                                        availableCategories={cart.availableCategories}
                                        products={cart.seatProducts}
                                        seatsPicked={cart.seats}
                                        onObjectSelected={onCartSeatAdd}
                                        onObjectDeselected={onCartSeatRemove}
                                        onSessionInitialized={onSessionInitialized}
                                        onSessionExpired={onSessionExpired}
                                    />
                                ) : (
                                    <Typography>
                                        No tickets found. Please ensure you have created at least
                                        one ticket before making an order.
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Box>
                )}
                <Box mb={4}>
                    <ProductPicker
                        title={seatsEnabled ? 'Other Tickets' : 'Build Order'}
                        products={cart.products}
                        bundles={cart.bundles}
                        error={emptyCart}
                        status={cart.status}
                        onProductUpdate={onCartProductUpdate}
                        onBundleUpdate={onCartBundleUpdate}
                    />
                </Box>
                <Box mb={4}>
                    <CompFormCustomer
                        customer={customer}
                        onChange={onCustomerUpdate}
                        validFields={validFields}
                    />
                </Box>

                <ButtonContainer>
                    <CancelButton onClick={onCancel}>Cancel</CancelButton>
                    <SubmitButton onClick={handleSubmit} disabled={sessionExpired}>
                        Next
                    </SubmitButton>
                </ButtonContainer>
            </FormContainer>
        </ScrollRestoreContainer>
    );
}

const FormContainer = styled(Box)(({ theme }) => ({
    width: theme.breakpoints.values.md,
    paddingBottom: theme.spacing(10),
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
}));
